import React, { type ReactNode } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { AtlassianMcpIcon } from './AtlassianMcpIcon';
import { TeamworkGraphIcon } from './TeamworkGraphIcon';

const messages = defineMessages({
	invokedBy: {
		id: 'agent-profile-card.external-agent.ExternalAgentAttribution.invokedBy',
		defaultMessage: 'Invoked by {name}',
		description:
			'Attribution line on an agent profile card naming the person the agent acted on behalf of.',
	},
	invokedByOthers: {
		id: 'agent-profile-card.external-agent.ExternalAgentAttribution.invokedByOthers',
		defaultMessage:
			'Invoked by {name} +{othersCount} {othersCount, plural, one {other} other {others}}',
		description:
			'Attribution line on an aggregate agent profile card naming the first person and counting the other people who invoked the agent.',
	},
	viaMcp: {
		id: 'agent-profile-card.external-agent.ExternalAgentAttribution.viaMcp',
		defaultMessage: 'using Atlassian MCP',
		description:
			'Attribution line on an agent profile card naming the channel the agent acted through.',
	},
	viaTeamworkGraph: {
		id: 'agent-profile-card.external-agent.ExternalAgentAttribution.viaTeamworkGraph',
		defaultMessage: 'using Teamwork Graph CLI',
		description:
			'Attribution line on an agent profile card naming the channel the agent acted through.',
	},
});

/**
 * Channels an external agent can reach a product through. Owned here rather than supplied by the
 * caller so the wording and glyph stay identical wherever this card is used.
 */
export type ExternalAgentChannel = 'mcp' | 'teamwork-graph';

const CHANNELS = {
	mcp: { message: messages.viaMcp, icon: <AtlassianMcpIcon size={14} /> },
	'teamwork-graph': { message: messages.viaTeamworkGraph, icon: <TeamworkGraphIcon size={15} /> },
} as const satisfies Record<ExternalAgentChannel, { message: unknown; icon: ReactNode }>;

const styles = cssMap({
	root: {
		minWidth: 0,
	},
	rootAggregate: {
		width: '100%',
	},
	row: {
		display: 'inline-flex',
		minWidth: 0,
		maxWidth: '100%',
	},
	rowSingle: {
		height: '24px',
	},
	rowAggregate: {
		minHeight: '24px',
		width: '100%',
	},
	// Avatar xxsmall paints its 16px face inside a 2px slot margin. Clipping to the face keeps this
	// row aligned with the 16px avatars rendered beside agent icons elsewhere in the product.
	faceClip: {
		display: 'block',
		width: '16px',
		height: '16px',
		flexShrink: 0,
		overflow: 'hidden',
		borderRadius: token('radius.full'),
	},
	facepileOutline: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		boxSizing: 'border-box',
		width: '20px',
		height: '20px',
		paddingBlockStart: token('space.025'),
		paddingBlockEnd: token('space.025'),
		paddingInlineStart: token('space.025'),
		paddingInlineEnd: token('space.025'),
		borderRadius: token('radius.full'),
		backgroundColor: token('elevation.surface'),
	},
	faceShift: {
		display: 'block',
		marginBlockStart: token('space.negative.025'),
		marginBlockEnd: token('space.negative.025'),
		marginInlineStart: token('space.negative.025'),
		marginInlineEnd: token('space.negative.025'),
	},
	facepile: {
		display: 'flex',
		alignItems: 'center',
		isolation: 'isolate',
	},
	facepileItem: {
		position: 'relative',
		marginInlineEnd: token('space.negative.050'),
	},
	facepileOverflow: {
		position: 'relative',
		flexShrink: 0,
		marginInlineEnd: token('space.negative.050'),
	},
	facepileOverflowCount: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	// Matches the avatar footprint so both rows share one text baseline regardless of glyph size.
	iconSlot: {
		width: '16px',
		height: '16px',
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	label: {
		minWidth: 0,
	},
	labelAggregate: {
		width: '100%',
	},
});

const AttributionRow = ({
	media,
	label,
	isAggregate = false,
	testId,
}: {
	media: ReactNode;
	label: string;
	isAggregate?: boolean;
	testId?: string;
}): React.JSX.Element => {
	const text = (
		<Box as="span" xcss={cx(styles.label, isAggregate && styles.labelAggregate)}>
			<Text size="small" color="color.text.subtlest" maxLines={isAggregate ? undefined : 1}>
				{label}
			</Text>
		</Box>
	);

	return isAggregate ? (
		<Stack xcss={styles.rowAggregate} space="space.075" testId={testId}>
			{media}
			{text}
		</Stack>
	) : (
		<Inline
			as="span"
			xcss={cx(styles.row, styles.rowSingle)}
			space="space.075"
			alignBlock="center"
			testId={testId}
		>
			{media}
			{text}
		</Inline>
	);
};

export type ExternalAgentInvokedBy = {
	/** Display name of the person the agent acted on behalf of. */
	name: string;
	/** Account ID used to open the person's profile card, when available. */
	accountId?: string;
	avatarUrl?: string;
};

export type ExternalAgentInvokedByInput =
	| ExternalAgentInvokedBy
	| readonly ExternalAgentInvokedBy[];

export type ExternalAgentInvokingUserRenderer = (
	user: ExternalAgentInvokedBy,
	content: ReactNode,
	visibleLabel?: string,
) => ReactNode;

export type ExternalAgentAttributionProps = {
	invokedBy?: ExternalAgentInvokedByInput;
	invokedVia?: ExternalAgentChannel;
	renderInvokingUser?: ExternalAgentInvokingUserRenderer;
	testId?: string;
};

/**
 * Attribution rows shown beneath an external agent's details: who the agent acted for, and the
 * channel it acted through. The channel is shown only when the invoking person is known.
 *
 * Callers pass attribution data and can supply their profile-card trigger for a known account ID.
 * The copy and glyphs live here so every product attributing an agent renders the same thing.
 */
export const ExternalAgentAttribution = ({
	invokedBy,
	invokedVia,
	renderInvokingUser,
	testId,
}: ExternalAgentAttributionProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const invokingUsers: readonly ExternalAgentInvokedBy[] = invokedBy
		? 'name' in invokedBy
			? [invokedBy]
			: invokedBy
		: [];
	const channel = invokingUsers.length > 0 && invokedVia ? CHANNELS[invokedVia] : undefined;

	if (invokingUsers.length === 0) {
		return null;
	}
	const renderPerson = (
		user: ExternalAgentInvokedBy,
		content: ReactNode,
		visibleLabel?: string,
	): ReactNode =>
		user.accountId && renderInvokingUser
			? renderInvokingUser(user, content, visibleLabel)
			: content;
	const avatar = (user: ExternalAgentInvokedBy, isInFacepile = false): ReactNode => {
		const face = (
			<Box as="span" xcss={styles.faceClip}>
				<Box as="span" xcss={styles.faceShift}>
					<Avatar size="xxsmall" src={user.avatarUrl} name="" />
				</Box>
			</Box>
		);
		return isInFacepile ? (
			<Box as="span" xcss={styles.facepileOutline}>
				{face}
			</Box>
		) : (
			face
		);
	};
	const isAggregate = invokingUsers.length > 1;
	const attributionLabel = formatMessage(
		isAggregate ? messages.invokedByOthers : messages.invokedBy,
		{ name: invokingUsers[0].name, othersCount: invokingUsers.length - 1 },
	);
	const personRow = (
		<AttributionRow
			testId={testId && `${testId}--invoked-by`}
			isAggregate={isAggregate}
			label={attributionLabel}
			media={
				isAggregate ? (
					<Box as="span" xcss={styles.facepile}>
						{/* Match AvatarGroup's stack order: the first face covers the next one. */}
						{invokingUsers.slice(0, 3).map((user, index) => (
							<Box
								as="span"
								key={user.accountId ?? `${user.name}:${index}`}
								xcss={styles.facepileItem}
								style={{ zIndex: 3 - index }}
								aria-hidden={user.accountId && renderInvokingUser ? undefined : true}
							>
								{renderPerson(user, avatar(user, true))}
							</Box>
						))}
						{invokingUsers.length > 3 && (
							<Box
								as="span"
								xcss={cx(styles.facepileOutline, styles.facepileOverflow)}
								aria-hidden="true"
							>
								<Box as="span" xcss={styles.facepileOverflowCount}>
									<Text size="small" weight="medium" color="color.text.subtle">
										{invokingUsers.length - 3}
									</Text>
								</Box>
							</Box>
						)}
					</Box>
				) : (
					avatar(invokingUsers[0])
				)
			}
		/>
	);

	return (
		<Stack
			xcss={cx(styles.root, isAggregate && styles.rootAggregate)}
			space={isAggregate ? 'space.100' : 'space.0'}
			testId={testId}
		>
			{isAggregate ? personRow : renderPerson(invokingUsers[0], personRow, attributionLabel)}
			{channel && (
				<AttributionRow
					testId={testId && `${testId}--invoked-via`}
					label={formatMessage(channel.message)}
					media={<Box xcss={styles.iconSlot}>{channel.icon}</Box>}
				/>
			)}
		</Stack>
	);
};
